import type { Question } from "$/modules/appwrite/types/collections";

const QUESTION_ORDER_STORAGE_PREFIX = "quizfy:question-order";

const getQuestionOrderStorageKey = (questionnaireId: string) =>
  `${QUESTION_ORDER_STORAGE_PREFIX}:${questionnaireId}`;

const getStoredQuestionOrder = (questionnaireId: string): string[] => {
  if (typeof window === "undefined") return [];

  try {
    const storedOrder = window.localStorage.getItem(
      getQuestionOrderStorageKey(questionnaireId),
    );

    if (!storedOrder) return [];

    const parsedOrder = JSON.parse(storedOrder);

    if (!Array.isArray(parsedOrder)) return [];

    return parsedOrder.filter((id): id is string => typeof id === "string");
  } catch {
    return [];
  }
};

export const sortQuestionsByStoredOrder = (
  questionnaireId: string,
  questions: Question[],
): Question[] => {
  if (questions.length === 0) return questions;

  const storedOrder = getStoredQuestionOrder(questionnaireId);
  if (storedOrder.length === 0) return questions;

  const questionsById = new Map(questions.map((question) => [question.$id, question]));

  const sortedQuestions = storedOrder
    .map((questionId) => questionsById.get(questionId))
    .filter((question): question is Question => Boolean(question));

  const missingQuestions = questions.filter(
    (question) => !storedOrder.includes(question.$id),
  );

  return [...sortedQuestions, ...missingQuestions];
};

export const persistQuestionOrder = (
  questionnaireId: string,
  questionIds: string[],
) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      getQuestionOrderStorageKey(questionnaireId),
      JSON.stringify(questionIds),
    );
  } catch {
    // Ignore storage failures and keep in-memory order only.
  }
};
